// Mailgun email validation service
// API documentation: https://documentation.mailgun.com/en/latest/api-email-validation.html

export interface MailgunValidationResult {
  address: string;
  is_disposable_address: boolean;
  is_role_address: boolean;
  reason: string[];
  result: 'deliverable' | 'undeliverable' | 'do_not_send' | 'catch-all' | 'unknown';
  risk: 'high' | 'medium' | 'low' | 'unknown';
  root_address?: string;
  did_you_mean?: string;
}

export interface MailgunValidationResponse {
  success: boolean;
  result?: MailgunValidationResult;
  error?: string;
}

export interface MailgunBulkValidationResult {
  email: string;
  valid: boolean;
  result?: MailgunValidationResult;
  error?: string;
}

/**
 * Validates a single email address using Mailgun's validation API
 */
export async function validateEmailWithMailgun(email: string, apiKey: string): Promise<MailgunValidationResponse> {
  try {
    // Mailgun validation endpoint
    const url = `https://api.mailgun.net/v4/address/validate`;

    // Create Basic Auth header
    const auth = btoa(`api:${apiKey}`);

    const response = await fetch(`${url}?address=${encodeURIComponent(email)}`, {
      method: 'GET',
      headers: {
        Authorization: `Basic ${auth}`,
      },
    });

    if (!response.ok) {
      const errorText = await response.text();
      return {
        success: false,
        error: `Mailgun API error: ${response.status} - ${errorText}`,
      };
    }

    const data = await response.json();

    return {
      success: true,
      result: data,
    };
  } catch (error: any) {
    return {
      success: false,
      error: error.message || 'Network error',
    };
  }
}

/**
 * Validates multiple email addresses in parallel with rate limiting
 */
export async function validateEmailsBulkWithMailgun(
  emails: string[],
  apiKey: string,
  onProgress?: (completed: number, total: number) => void,
): Promise<MailgunBulkValidationResult[]> {
  const results: MailgunBulkValidationResult[] = [];

  // Process in batches to avoid rate limiting
  const BATCH_SIZE = 5;
  const DELAY_MS = 200; // Delay between batches

  for (let i = 0; i < emails.length; i += BATCH_SIZE) {
    const batch = emails.slice(i, i + BATCH_SIZE);

    const batchPromises = batch.map(async (email) => {
      const response = await validateEmailWithMailgun(email, apiKey);

      if (response.success && response.result) {
        const isValid = response.result.result === 'deliverable' || (response.result.result === 'catch-all' && response.result.risk === 'low');

        return {
          email,
          valid: isValid,
          result: response.result,
        };
      } else {
        return {
          email,
          valid: false,
          error: response.error,
        };
      }
    });

    const batchResults = await Promise.all(batchPromises);
    results.push(...batchResults);

    if (onProgress) {
      onProgress(results.length, emails.length);
    }

    // Delay before next batch (except for the last batch)
    if (i + BATCH_SIZE < emails.length) {
      await new Promise((resolve) => setTimeout(resolve, DELAY_MS));
    }
  }

  return results;
}

/**
 * Gets a human-readable description of validation result
 */
export function getMailgunResultDescription(result?: MailgunValidationResult): string {
  if (!result) return 'Unknown';

  const {result: status, risk, is_disposable_address, is_role_address, reason} = result;

  if (status === 'deliverable') {
    if (risk === 'high') return 'Deliverable but high risk';
    if (risk === 'medium') return 'Deliverable but medium risk';
    return 'Deliverable';
  }

  if (status === 'undeliverable') {
    return `Undeliverable: ${reason.join(', ')}`;
  }

  if (status === 'do_not_send') {
    if (is_disposable_address) return 'Disposable email address';
    if (is_role_address) return 'Role-based email address';
    return 'Do not send';
  }

  if (status === 'catch-all') {
    return `Catch-all domain (${risk} risk)`;
  }

  return 'Unable to verify';
}

/**
 * Determines if an email should be considered valid based on Mailgun result
 */
export function isMailgunResultValid(result: MailgunValidationResult): boolean {
  // Deliverable emails are valid
  if (result.result === 'deliverable') {
    return true;
  }

  // Catch-all with low risk can be considered valid
  if (result.result === 'catch-all' && result.risk === 'low') {
    return true;
  }

  return false;
}

/**
 * Gets risk level color class for UI
 */
export function getMailgunRiskColor(risk?: string): string {
  switch (risk) {
    case 'low':
      return 'text-green-600 dark:text-green-400';
    case 'medium':
      return 'text-yellow-600 dark:text-yellow-400';
    case 'high':
      return 'text-red-600 dark:text-red-400';
    default:
      return 'text-gray-600 dark:text-gray-400';
  }
}

/**
 * Gets result status color class for UI
 */
export function getMailgunResultColor(result?: string): string {
  switch (result) {
    case 'deliverable':
      return 'text-green-600 dark:text-green-400';
    case 'undeliverable':
    case 'do_not_send':
      return 'text-red-600 dark:text-red-400';
    case 'catch-all':
      return 'text-yellow-600 dark:text-yellow-400';
    default:
      return 'text-gray-600 dark:text-gray-400';
  }
}
